//***********************************************************
// Angular
//***********************************************************
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

//***********************************************************
// MJ - Consolidated Module Bundles
//***********************************************************
import {
  MJExplorerModulesBundle,
  SharedService
} from '@memberjunction/ng-explorer-modules';
import { AuthServicesModule, RedirectComponent, MJAuthBase } from '@memberjunction/ng-auth-services';
import { MJExplorerAppModule } from '@memberjunction/ng-explorer-app';

// Import pre-built MJ class registrations manifest (covers all @memberjunction/* packages)
import {CLASS_REGISTRATIONS} from '@memberjunction/ng-bootstrap';

// Import supplemental manifest for user-defined classes (generated at prestart with --exclude-packages @memberjunction)
import {CLASS_REGISTRATIONS as LOCAL_CLASSES} from './generated/class-registrations-manifest';

// static code path builder - prevents tree-shaking of dynamically loaded components
const combinedClasses = [...CLASS_REGISTRATIONS, ...LOCAL_CLASSES];

//***********************************************************
//MSAL
//***********************************************************
import { MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';

//***********************************************************
// BCSaaS Modules
//***********************************************************
import { MessageService } from 'primeng/api';
import { BCCoreModule } from '@bluecypress/bcsaas-ng-core';
import { NgOnboardingModule } from '@bluecypress/bcsaas-ng-onboarding';
import { NgProfileModule } from '@bluecypress/bcsaas-ng-profile';
import { NgOrgAdminModule } from '@bluecypress/bcsaas-ng-org-admin';
import { NgPlatformAdminModule } from '@bluecypress/bcsaas-ng-platform-admin';

//***********************************************************
// Project stuff
//***********************************************************
import { AppComponent } from './app.component';
import { GeneratedFormsModule } from './generated/generated-forms.module';
import { environment } from '../environments/environment';
import { NavigationItemDemoComponent } from './demo/navigation-item.component';
import { HelloDashboardComponent } from './demo/hello-dashboard/hello-dashboard.component';
import { NotesResourceComponent } from './notes-resource.component';
import { BCProfileNavComponent } from './bc-profile-nav.component';
import { SkipAnalyticsComponent } from './skip-analytics/skip-analytics.component';
import { SkipMonitoringOverviewComponent } from './skip-monitoring/overview/overview.component';
import { SkipMonitoringOrgsComponent } from './skip-monitoring/organizations/organizations.component';
import { SkipMonitoringAgentRunsComponent } from './skip-monitoring/agent-runs/agent-runs.component';
import { SkipMonitoringCostsComponent } from './skip-monitoring/costs/costs.component';

// Note: LoadGeneratedForms() removed - tree-shaking prevention is now handled by
// the pre-built class registration manifest system.

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
  };
}

/**
 * Initialize auth provider before Angular routing starts
 * This ensures MSAL can process OAuth redirect responses before Angular's router
 * consumes the URL hash
 */
export function InitializeAuth(authService: MJAuthBase): () => Promise<void> {
  return () => authService.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    NavigationItemDemoComponent,
    HelloDashboardComponent,
    NotesResourceComponent,
    BCProfileNavComponent,
    SkipAnalyticsComponent,
    SkipMonitoringOverviewComponent,
    SkipMonitoringOrgsComponent,
    SkipMonitoringAgentRunsComponent,
    SkipMonitoringCostsComponent
  ],
  imports: [
    // Angular Core Modules
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,

    // MJ Consolidated Bundle (includes all MJ + Kendo modules)
    MJExplorerModulesBundle,

    // Auth (needs forRoot configuration)
    AuthServicesModule.forRoot(environment),

    // Explorer App Shell (includes login UI, validation, and mj-shell wrapper)
    MJExplorerAppModule.forRoot(environment),

    // BCSaaS modules
    BCCoreModule.forRoot(),
    NgOnboardingModule,
    NgProfileModule,
    NgOrgAdminModule,
    NgPlatformAdminModule,

    // App-specific modules
    GeneratedFormsModule
  ],
  providers: [
    SharedService,
    MessageService,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: InitializeAuth,
      deps: [MJAuthBase],
      multi: true
    }
  ],
  bootstrap: [AppComponent, RedirectComponent],
})
export class AppModule {}
